import * as React from "react";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import ADMIN_ACTIONS from "../actions/AdminActions";
import {
  REACT_APP_API_BASE_URL,
  SNACKBAR_VARIANT,
} from "../../config";
import ADMIN_MESSAGES from "../../config/adminMessages";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";
import useCustomSnackbar from "../../store/contexts/SnackbarContext";

export const AdminContext = React.createContext();

function useAdminContext() {
  const context = React.useContext(AdminContext);

  if (!context) {
    throw new Error("useAdminContext must be used within an AdminAuthProvider");
  }

  const { showSnackbar } = useCustomSnackbar();
  const history = useHistory();
  const location = useLocation();
  const currentPageURL = location.pathname;

  const [adminState, dispatch] = context;

  const {
    accessToken,
    refreshToken,
    userData,
    adminList,
    dataLoading,
    error,
    message,
    adminActivity,
    activitySummary,
  } = adminState;

  const {
    ADMIN_DATA_LOADING,
    SET_ADMIN_AUTH_TOKENS,
    SET_ADMIN_USER_DATA,
    ADMIN_LOGOUT,
    SET_ADMIN_ERROR,
    CLEAR_ADMIN_ERRORS,
    SET_ADMIN_MESSAGE,
    SET_ADMIN_LIST,
    UPDATE_ADMIN_DATA,
    SET_ADMIN_ACTIVITY,
    SET_ADMIN_ACTIVITY_SUMMARY,
  } = ADMIN_ACTIONS;

  axios.defaults.baseURL = REACT_APP_API_BASE_URL;

  const URLsBeforeLogin = [
    ADMIN_ROUTES.login,
    ADMIN_ROUTES.forgotPassword,
    ADMIN_ROUTES.resetPassword,
    ADMIN_ROUTES.root,
  ];

  const isAdminRoute = currentPageURL.startsWith("/admin");

  React.useEffect(() => {
    if (
      accessToken &&
      !URLsBeforeLogin.includes(currentPageURL) &&
      isAdminRoute &&
      Object.keys(userData).length === 0
    ) {
      getAdminAccountApiCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dataLoadinHandler = (status) => {
    dispatch({
      type: ADMIN_DATA_LOADING,
      payload: status,
    });
  };

  const setAuthTokens = ({ accessToken, refreshToken }) => {
    dispatch({
      type: SET_ADMIN_AUTH_TOKENS,
      payload: { accessToken, refreshToken },
    });
  };

  const setAdminUserData = (data) => {
    dispatch({
      type: SET_ADMIN_USER_DATA,
      payload: data,
    });
  };

  const logout = () => {
    dispatch({
      type: ADMIN_LOGOUT,
    });
  };

  const resetApp = () => {
    logout();
    return loadANewPage(ADMIN_ROUTES.root);
  };

  const loadANewPage = (page) => {
    return (window.location.href = page);
  };

  const errorMessageHandler = (message) => {
    return message && typeof message === "string"
      ? message
      : ADMIN_MESSAGES.UNEXPECTED_ERROR;
  };

  const clearErrors = () => {
    dispatch({
      type: CLEAR_ADMIN_ERRORS,
    });
  };

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  const loginAdminApiCall = async (options) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post("/admin/auth/login", options);
      setAuthTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || "",
      });
      dataLoadinHandler(false);
      showSnackbar(ADMIN_MESSAGES.LOGIN_SUCCESSFUL, SNACKBAR_VARIANT.SUCCESS);
      return loadANewPage(ADMIN_ROUTES.dashboard);
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const forgotPasswordAdminApiCall = async (options) => {
    try {
      dataLoadinHandler(true);
      const data = await axios.post("/admin/auth/forgot-password", options);
      dataLoadinHandler(false);

      dispatch({
        type: SET_ADMIN_MESSAGE,
        payload: data.data.message,
      });
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const resetPasswordAdminApiCall = async (formData) => {
    try {
      dataLoadinHandler(true);
      const { token, password, confirm_password } = formData;

      await axios.post("/admin/auth/reset-password", {
        password,
        confirm_password,
        token,
      });

      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_MESSAGE,
        payload: ADMIN_MESSAGES.RESET_PASSWORD_SUCCESSFUL,
      });
      showSnackbar(
        ADMIN_MESSAGES.RESET_PASSWORD_SUCCESSFUL,
        SNACKBAR_VARIANT.SUCCESS,
      );
      return true;
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const changePasswordAdminApiCall = async (options) => {
    try {
      dataLoadinHandler(true);
      await axios.post("/admin/auth/change-password", options, { headers });
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_MESSAGE,
        payload: ADMIN_MESSAGES.PASSWORD_CHANGED,
      });
      return showSnackbar(
        ADMIN_MESSAGES.PASSWORD_CHANGED,
        SNACKBAR_VARIANT.SUCCESS,
      );
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getAdminAccountApiCall = async () => {
    dataLoadinHandler(true);
    try {
      const admin = await axios.get("/admin/account", { headers });
      dataLoadinHandler(false);
      return setAdminUserData(admin.data.data);
    } catch (err) {
      return refreshAdminTokenApiCall();
    }
  };

  const refreshAdminTokenApiCall = async () => {
    try {
      const { data } = await axios.get(
        `/admin/auth/refresh-token?token=${refreshToken}`,
      );
      setAuthTokens({
        accessToken: data.accessToken,
        refreshToken: refreshToken,
      });
      const admin = await axios.get("/admin/account", {
        headers: { ...headers, Authorization: `Bearer ${data.accessToken}` },
      });
      dataLoadinHandler(false);
      return setAdminUserData(admin.data.data);
    } catch (err) {
      dataLoadinHandler(false);
      logout();
      return history.push(ADMIN_ROUTES.login);
    }
  };

  const listAdminsApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();
      if (filters.role) queryParams.append("role", filters.role);
      if (filters.is_active !== undefined)
        queryParams.append("is_active", filters.is_active);
      if (filters.is_suspended !== undefined)
        queryParams.append("is_suspended", filters.is_suspended);
      queryParams.append("pageNo", filters.pageNo || 1);
      queryParams.append("pageSize", filters.pageSize || 20);

      const { data } = await axios.get(
        `/admin/list?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);

      return {
        admins: data.data.admins,
        pagination: data.data.pagination,
      };
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const createAdminApiCall = async (data) => {
    try {
      dataLoadinHandler(true);
      const result = await axios.post("/admin/create", data, { headers });
      dataLoadinHandler(false);
      showSnackbar(ADMIN_MESSAGES.ADMIN_CREATED, SNACKBAR_VARIANT.SUCCESS);
      return result.data.data;
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getAdminByIdApiCall = async (id) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get(`/admin/${id}`, { headers });
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return null;
    }
  };

  const updateAdminApiCall = async (id, data) => {
    try {
      dataLoadinHandler(true);
      const result = await axios.put(`/admin/${id}`, data, { headers });

      dataLoadinHandler(false);
      showSnackbar(ADMIN_MESSAGES.ADMIN_UPDATED, SNACKBAR_VARIANT.SUCCESS);
      return result.data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const suspendAdminApiCall = async (id, data) => {
    try {
      dataLoadinHandler(true);
      const result = await axios.patch(`/admin/${id}/suspend`, data, {
        headers,
      });

      dataLoadinHandler(false);
      showSnackbar(
        data.is_suspended
          ? ADMIN_MESSAGES.ADMIN_SUSPENDED
          : ADMIN_MESSAGES.ADMIN_UNSUSPENDED,
        SNACKBAR_VARIANT.SUCCESS,
      );
      return result.data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const toggleAdminStatusApiCall = async (id, data) => {
    try {
      dataLoadinHandler(true);
      const result = await axios.patch(`/admin/${id}/status`, data, {
        headers,
      });

      dataLoadinHandler(false);
      showSnackbar(
        data.is_active
          ? ADMIN_MESSAGES.ADMIN_ACTIVATED
          : ADMIN_MESSAGES.ADMIN_DEACTIVATED,
        SNACKBAR_VARIANT.SUCCESS,
      );
      return result.data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getActivityLogsApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (filters.adminId) {
        queryParams.append("adminId", filters.adminId);
      }
      if (filters.action && filters.action !== "all") {
        queryParams.append("action", filters.action);
      }
      if (filters.dateFrom) {
        queryParams.append("dateFrom", filters.dateFrom);
      }
      if (filters.dateTo) {
        queryParams.append("dateTo", filters.dateTo);
      }
      if (filters.recent) {
        queryParams.append("recent", filters.recent);
      }
      if (filters.recentLimit) {
        queryParams.append("recentLimit", filters.recentLimit);
      }
      queryParams.append("pageNo", filters.pageNo || 1);
      queryParams.append("pageSize", filters.pageSize || 50);

      const { data } = await axios.get(
        `/admin/activity/logs?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return {
        activities: data.data.activities || data.data.logs,
        logs: data.data.logs || data.data.activities,
        pagination: data.data.pagination,
        isRecent: data.data.isRecent || false,
      };
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getActivitySummaryApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (filters.adminId) {
        queryParams.append("adminId", filters.adminId);
      }
      if (filters.dateFrom) {
        queryParams.append("dateFrom", filters.dateFrom);
      }
      if (filters.dateTo) {
        queryParams.append("dateTo", filters.dateTo);
      }

      const { data } = await axios.get(
        `/admin/activity/summary?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getRecentActivityApiCall = async (filters = {}) => {
    return getActivityLogsApiCall({
      ...filters,
      recent: true,
      recentLimit: filters.pageSize || 10,
    });
  };

  const getAdminActivityLogsApiCall = async (adminId, filters = {}) => {
    return getActivityLogsApiCall({
      ...filters,
      adminId,
    });
  };

  const getDashboardOverviewApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/admin/dashboard/overview", {
        headers,
      });
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getUsersListApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append("search", filters.search);
      if (filters.status && filters.status !== "all")
        queryParams.append("status", filters.status);
      if (filters.account_type && filters.account_type !== "all")
        queryParams.append("account_type", filters.account_type);
      if (filters.is_banned && filters.is_banned !== "all")
        queryParams.append("is_banned", filters.is_banned);
      queryParams.append("pageNo", filters.pageNo || 1);
      queryParams.append("pageSize", filters.pageSize || 20);

      const { data } = await axios.get(
        `/admin/users?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return {
        users: data.data.users,
        pagination: data.data.pagination,
      };
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getUserByIdApiCall = async (id) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get(`/admin/users/${id}`, { headers });
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return null;
    }
  };

  const updateUserApiCall = async (id, updateData) => {
    try {
      dataLoadinHandler(true);
      const result = await axios.put(`/admin/users/${id}`, updateData, {
        headers,
      });
      dataLoadinHandler(false);
      showSnackbar("User updated successfully", SNACKBAR_VARIANT.SUCCESS);
      return result.data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const toggleUserStatusApiCall = async (id, statusData) => {
    try {
      dataLoadinHandler(true);
      const result = await axios.patch(
        `/admin/users/${id}/status`,
        statusData,
        {
          headers,
        },
      );
      dataLoadinHandler(false);
      showSnackbar(
        statusData.is_banned
          ? "User banned successfully"
          : "User unbanned successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return result.data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getRoomsListApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append("search", filters.search);
      if (filters.type && filters.type !== "all")
        queryParams.append("type", filters.type);
      if (filters.status && filters.status !== "all")
        queryParams.append("status", filters.status);
      if (filters.is_active && filters.is_active !== "all")
        queryParams.append("is_active", filters.is_active);
      if (filters.dateFrom) queryParams.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) queryParams.append("dateTo", filters.dateTo);
      queryParams.append("pageNo", filters.pageNo || 1);
      queryParams.append("pageSize", filters.pageSize || 20);

      const { data } = await axios.get(
        `/admin/rooms?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return {
        rooms: data.data.rooms,
        pagination: data.data.pagination,
      };
    } catch (err) {
      dataLoadinHandler(false);
      // dispatch({
      //   type: SET_ADMIN_ERROR,
      //   payload: errorMessageHandler(err?.response?.data?.message),
      // });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getRoomByIdApiCall = async (id) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get(`/admin/rooms/${id}`, { headers });
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return null;
    }
  };

  // Update room API call
  const updateRoomApiCall = async (id, updateData) => {
    try {
      dataLoadinHandler(true);
      const result = await axios.put(`/admin/rooms/${id}`, updateData, {
        headers,
      });
      dataLoadinHandler(false);
      showSnackbar("Room updated successfully", SNACKBAR_VARIANT.SUCCESS);
      return result.data.data;
    } catch (err) {
      dataLoadinHandler(false);
      const errorMessage =
        err?.response?.data?.message || "Failed to update room";
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(errorMessage),
      });
      return showSnackbar(
        errorMessageHandler(errorMessage),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Toggle room status API call
  const toggleRoomStatusApiCall = async (id, statusData) => {
    try {
      dataLoadinHandler(true);
      const result = await axios.patch(
        `/admin/rooms/${id}/status`,
        statusData,
        {
          headers,
        },
      );
      dataLoadinHandler(false);
      showSnackbar(
        statusData.is_active
          ? "Room activated successfully"
          : "Room deactivated successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return result.data.data;
    } catch (err) {
      dataLoadinHandler(false);
      const errorMessage =
        err?.response?.data?.message || "Failed to update room status";
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(errorMessage),
      });
      return showSnackbar(
        errorMessageHandler(errorMessage),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // ===== SESSION MANAGEMENT API CALLS =====
  const getActiveSessionsApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (filters.pageNo) queryParams.append("pageNo", filters.pageNo);
      if (filters.pageSize) queryParams.append("pageSize", filters.pageSize);
      if (filters.userType && filters.userType !== "all")
        queryParams.append("userType", filters.userType);
      if (filters.userId) queryParams.append("userId", filters.userId);
      if (filters.email) queryParams.append("email", filters.email);
      if (filters.username) queryParams.append("username", filters.username);
      if (filters.ipAddress) queryParams.append("ipAddress", filters.ipAddress);

      const { data } = await axios.get(
        `/admin/sessions/active?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getSessionStatisticsApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/admin/sessions/statistics", {
        headers,
      });
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const terminateSessionApiCall = async (sessionId) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.delete(`/admin/sessions/${sessionId}`, {
        headers,
      });
      dataLoadinHandler(false);
      showSnackbar("Session terminated successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const cleanupExpiredSessionsApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post(
        "/admin/sessions/cleanup",
        {},
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        "Expired sessions cleaned up successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // ===== PAYMENT MANAGEMENT API CALLS =====
  const getPaymentsListApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (filters.pageNo) queryParams.append("pageNo", filters.pageNo);
      if (filters.pageSize) queryParams.append("pageSize", filters.pageSize);
      if (filters.status && filters.status !== "all")
        queryParams.append("status", filters.status);
      if (filters.payment_type && filters.payment_type !== "all")
        queryParams.append("payment_type", filters.payment_type);
      if (filters.payment_provider && filters.payment_provider !== "all")
        queryParams.append("payment_provider", filters.payment_provider);
      if (filters.user_id) queryParams.append("user_id", filters.user_id);
      if (filters.dateFrom) queryParams.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) queryParams.append("dateTo", filters.dateTo);
      if (filters.amount_min)
        queryParams.append("amount_min", filters.amount_min);
      if (filters.amount_max)
        queryParams.append("amount_max", filters.amount_max);
      if (filters.search) queryParams.append("search", filters.search);

      const { data } = await axios.get(
        `/admin/payments/all?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getPaymentStatisticsApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (filters.dateFrom) queryParams.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) queryParams.append("dateTo", filters.dateTo);

      const { data } = await axios.get(
        `/admin/payments/statistics?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getPaymentByIdApiCall = async (id) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get(`/admin/payments/${id}`, { headers });
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return null;
    }
  };

  const updatePaymentStatusApiCall = async (id, statusData) => {
    try {
      dataLoadinHandler(true);
      const result = await axios.patch(
        `/admin/payments/${id}/status`,
        statusData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar("Payment updated successfully", SNACKBAR_VARIANT.SUCCESS);
      return result.data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // ===== ANALYTICS API CALLS =====
  const getUserAnalyticsApiCall = async (dateFrom = null, dateTo = null) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (dateFrom) queryParams.append("dateFrom", dateFrom);
      if (dateTo) queryParams.append("dateTo", dateTo);

      const { data } = await axios.get(
        `/admin/analytics/users?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getRoomAnalyticsApiCall = async (dateFrom = null, dateTo = null) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (dateFrom) queryParams.append("dateFrom", dateFrom);
      if (dateTo) queryParams.append("dateTo", dateTo);

      const { data } = await axios.get(
        `/admin/analytics/rooms?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getPaymentAnalyticsApiCall = async (dateFrom = null, dateTo = null) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (dateFrom) queryParams.append("dateFrom", dateFrom);
      if (dateTo) queryParams.append("dateTo", dateTo);

      const { data } = await axios.get(
        `/admin/analytics/payments?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getDashboardAnalyticsApiCall = async (
    dateFrom = null,
    dateTo = null,
  ) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (dateFrom) queryParams.append("dateFrom", dateFrom);
      if (dateTo) queryParams.append("dateTo", dateTo);

      const { data } = await axios.get(
        `/admin/analytics/dashboard?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // ===== PLATFORM ECONOMY SETTINGS API CALLS =====
  const getAllEconomySettingsApiCall = async (category = null) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();
      if (category) queryParams.append("category", category);

      const { data } = await axios.get(
        `/admin/platform/economy/settings?${queryParams.toString()}`,
        { headers },
      );
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Get platform economy statistics
  const getEconomyStatisticsApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/admin/platform/economy/statistics", {
        headers,
      });
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Get coin economy settings
  const getCoinEconomyApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/admin/platform/coin-economy", {
        headers,
      });
      dataLoadinHandler(false);
      return data.data.coin_economy;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Update coin economy settings
  const updateCoinEconomyApiCall = async (coinEconomyData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        "/admin/platform/coin-economy",
        coinEconomyData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        "Coin economy settings updated successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return data.data.coin_economy;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Get room limits settings
  const getRoomLimitsApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/admin/platform/room-limits", {
        headers,
      });
      dataLoadinHandler(false);
      return data.data.room_limits;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Update room limits settings
  const updateRoomLimitsApiCall = async (roomLimitsData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        "/admin/platform/room-limits",
        roomLimitsData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        "Room limits updated successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return data.data.room_limits;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Get XP rewards settings
  const getXPRewardsApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/admin/platform/xp-rewards", {
        headers,
      });
      dataLoadinHandler(false);
      return data.data.xp_rewards;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Update XP rewards settings
  const updateXPRewardsApiCall = async (xpRewardsData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        "/admin/platform/xp-rewards",
        xpRewardsData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar("XP rewards updated successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data.xp_rewards;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Get general platform settings
  const getGeneralSettingsApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/admin/platform/general", { headers });
      dataLoadinHandler(false);
      return data.data.general;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Update general platform settings
  const updateGeneralSettingsApiCall = async (generalData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put("/admin/platform/general", generalData, {
        headers,
      });
      dataLoadinHandler(false);
      showSnackbar(
        "General settings updated successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return data.data.general;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Get fee limits settings
  const getFeeLimitsApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/admin/platform/fee-limits", {
        headers,
      });
      dataLoadinHandler(false);
      return data.data.fee_limits;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Update fee limits settings
  const updateFeeLimitsApiCall = async (feeLimitsData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        "/admin/platform/fee-limits",
        feeLimitsData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar("Fee limits updated successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data.fee_limits;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Bulk update multiple settings
  const updateBulkSettingsApiCall = async (settingsArray) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        "/admin/platform/economy/settings/bulk",
        { settings: settingsArray },
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar("Settings updated successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Create new setting
  const createNewSettingApiCall = async (settingData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post(
        "/admin/platform/economy/settings",
        settingData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar("Setting created successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data.setting;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Get specific setting by key
  const getSettingByKeyApiCall = async (settingKey) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get(
        `/admin/platform/economy/settings/${settingKey}`,
        { headers },
      );
      dataLoadinHandler(false);
      return data.data.setting;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Update specific setting by key
  const updateSettingByKeyApiCall = async (settingKey, settingData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        `/admin/platform/economy/settings/${settingKey}`,
        settingData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar("Setting updated successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data.setting;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // Delete specific setting by key
  const deleteSettingByKeyApiCall = async (settingKey) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.delete(
        `/admin/platform/economy/settings/${settingKey}`,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar("Setting deleted successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // ===== COIN PACKAGES API CALLS =====
  const getAllCoinPackagesApiCall = async (isActive = null) => {
    try {
      dataLoadinHandler(true);
      const params = new URLSearchParams();
      if (isActive !== null) {
        params.append("is_active", isActive);
      }

      const { data } = await axios.get(
        `/admin/platform/coin-packages?${params.toString()}`,
        { headers },
      );
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const createCoinPackageApiCall = async (packageData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post(
        "/admin/platform/coin-packages",
        packageData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        "Coin package created successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const updateCoinPackageApiCall = async (id, packageData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        `/admin/platform/coin-packages/${id}`,
        packageData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        "Coin package updated successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const deleteCoinPackageApiCall = async (id) => {
    try {
      dataLoadinHandler(true);
      await axios.delete(`/admin/platform/coin-packages/${id}`, { headers });
      dataLoadinHandler(false);
      showSnackbar(
        "Coin package deleted successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return true;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // ===== SUBSCRIPTION PLANS API CALLS =====
  const getAllSubscriptionPlansApiCall = async (isActive = null) => {
    try {
      dataLoadinHandler(true);
      const params = new URLSearchParams();
      if (isActive !== null) {
        params.append("is_active", isActive);
      }

      const { data } = await axios.get(
        `/admin/platform/subscription-plans?${params.toString()}`,
        { headers },
      );
      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const createSubscriptionPlanApiCall = async (planData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post(
        "/admin/platform/subscription-plans",
        planData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        "Subscription plan created successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const updateSubscriptionPlanApiCall = async (id, planData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        `/admin/platform/subscription-plans/${id}`,
        planData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        "Subscription plan updated successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const deleteSubscriptionPlanApiCall = async (id) => {
    try {
      dataLoadinHandler(true);
      await axios.delete(`/admin/platform/subscription-plans/${id}`, {
        headers,
      });
      dataLoadinHandler(false);
      showSnackbar(
        "Subscription plan deleted successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return true;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // ===== SYSTEM CONFIG API CALLS =====
  const getSystemConfigApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/admin/platform/system-config", {
        headers,
      });
      dataLoadinHandler(false);
      return data.data.system_config;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const updateSystemConfigApiCall = async (configData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        "/admin/platform/system-config",
        configData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        "System config updated successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return data.data.system_config;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getSystemHealthMetricsApiCall = async () => {
    try {
      dataLoadinHandler(true);

      const { data } = await axios.get("/admin/analytics/system-health", {
        headers,
      });

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const getLoginActivityAnalyticsApiCall = async (
    dateFrom = null,
    dateTo = null,
  ) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      if (dateFrom) queryParams.append("dateFrom", dateFrom);
      if (dateTo) queryParams.append("dateTo", dateTo);

      const { data } = await axios.get(
        `/admin/analytics/login-activity?${queryParams.toString()}`,
        { headers },
      );

      dataLoadinHandler(false);
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const exportPaymentsApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const queryParams = new URLSearchParams();

      Object.keys(filters).forEach((key) => {
        if (filters[key] && filters[key] !== "all") {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await axios.get(
        `/admin/payments/export?${queryParams.toString()}`,
        {
          headers,
          responseType: "blob",
        },
      );

      // Create download link
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `payments-export-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dataLoadinHandler(false);
      showSnackbar("Payments exported successfully", SNACKBAR_VARIANT.SUCCESS);
      return true;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  // ============= AD MANAGEMENT API CALLS =============

  /**
   * Get all ads created by the admin
   * GET /ads/admin/list
   */
  const getAdminAdsApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/ads/admin/list", { headers });
      dataLoadinHandler(false);
      return data.data || data;
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  /**
   * Get analytics for all admin ads
   * GET /ads/admin/analytics
   */
  const getAdAnalyticsApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get("/ads/admin/analytics", { headers });
      dataLoadinHandler(false);
      return data.data || data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  /**
   * Create a new ad
   * POST /ads/create
   * @param {Object} adData - Ad creation data
   */
  const createAdApiCall = async (adData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post("/ads/create", adData, { headers });
      dataLoadinHandler(false);
      showSnackbar("Ad created successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data || data;
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  /**
   * Get single ad details
   * GET /ads/admin/:adId
   * @param {string} adId - Ad ID
   */
  const getAdByIdApiCall = async (adId) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get(`/ads/admin/${adId}`, { headers });
      dataLoadinHandler(false);
      return data.data || data;
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return null;
    }
  };

  /**
   * Update an existing ad
   * PUT /ads/admin/:adId
   * @param {string} adId - Ad ID
   * @param {Object} updateData - Updated ad data
   */
  const updateAdApiCall = async (adId, updateData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(`/ads/admin/${adId}`, updateData, {
        headers,
      });
      dataLoadinHandler(false);
      showSnackbar("Ad updated successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data || data;
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  /**
   * Delete an ad
   * DELETE /ads/admin/:adId
   * @param {string} adId - Ad ID
   */
  const deleteAdApiCall = async (adId) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.delete(`/ads/admin/${adId}`, { headers });
      dataLoadinHandler(false);
      showSnackbar("Ad deleted successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data || data;
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  /**
   * Activate an ad
   * POST /ads/admin/:adId/activate
   * @param {string} adId - Ad ID
   */
  const activateAdApiCall = async (adId) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post(
        `/ads/admin/${adId}/activate`,
        {},
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar("Ad activated successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data || data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  /**
   * Pause an ad
   * POST /ads/admin/:adId/pause
   * @param {string} adId - Ad ID
   */
  const pauseAdApiCall = async (adId) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post(
        `/ads/admin/${adId}/pause`,
        {},
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar("Ad paused successfully", SNACKBAR_VARIANT.SUCCESS);
      return data.data || data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  /**
   * Check if ad has reached impression goal
   * GET /ads/admin/:adId/check-goal
   * @param {string} adId - Ad ID
   */
  const checkAdImpressionGoalApiCall = async (adId) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get(`/ads/admin/${adId}/check-goal`, {
        headers,
      });
      dataLoadinHandler(false);
      return data.data || data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const logoutAdminApiCall = async () => {
    logout();
    showSnackbar(ADMIN_MESSAGES.LOGOUT_SUCCESSFUL, SNACKBAR_VARIANT.SUCCESS);
    return true;
  };

  // ============================================
  // NOTIFICATION API CALLS
  // ============================================

  /**
   * Get all notifications with pagination and filters
   * GET /admin/notifications
   * @param {Object} filters - { pageNo, pageSize, type, priority, is_active, admin_id }
   */
  const getNotificationsApiCall = async (filters = {}) => {
    try {
      dataLoadinHandler(true);
      const params = {
        pageNo: filters.pageNo || 1,
        pageSize: filters.pageSize || 20,
      };

      if (filters.type) params.type = filters.type;
      if (filters.priority) params.priority = filters.priority;
      if (filters.is_active !== undefined) params.is_active = filters.is_active;
      if (filters.admin_id) params.admin_id = filters.admin_id;

      const { data } = await axios.get("/admin-notifications", {
        params,
        headers,
      });

      dataLoadinHandler(false);
      return { success: true, data: data.data };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  /**
   * Get single notification by ID
   * GET /admin/notifications/:id
   * @param {string} notificationId - Notification ID
   */
  const getNotificationByIdApiCall = async (notificationId) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.get(
        `/admin-notifications/${notificationId}`,
        {
          headers,
        },
      );
      dataLoadinHandler(false);
      return { success: true, data: data.data };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  /**
   * Create a new notification
   * POST /admin/notifications
   * @param {Object} notificationData - { title, message, type, priority, is_active }
   */
  const createNotificationApiCall = async (notificationData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post(
        "/admin-notifications",
        notificationData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        data.message || "Notification created successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return { success: true, data: data.data };
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  /**
   * Update an existing notification
   * PUT /admin/notifications/:id
   * @param {string} notificationId - Notification ID
   * @param {Object} updateData - Updated notification data
   */
  const updateNotificationApiCall = async (notificationId, updateData) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.put(
        `/admin-notifications/${notificationId}`,
        updateData,
        { headers },
      );
      dataLoadinHandler(false);
      showSnackbar(
        data.message || "Notification updated successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return { success: true, data: data.data };
    } catch (err) {
      dataLoadinHandler(false);
      dispatch({
        type: SET_ADMIN_ERROR,
        payload: errorMessageHandler(err?.response?.data?.message),
      });
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  /**
   * Delete a notification
   * DELETE /admin/notifications/:id
   * @param {string} notificationId - Notification ID
   */
  const deleteNotificationApiCall = async (notificationId) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.delete(
        `/admin-notifications/${notificationId}`,
        {
          headers,
        },
      );
      dataLoadinHandler(false);
      showSnackbar(
        data.message || "Notification deleted successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );
      return { success: true };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  return {
    authState: {
      ...adminState,
      loading: dataLoading,
    },
    admin: userData,
    loginAdminApiCall,
    forgotPasswordAdminApiCall,
    resetPasswordAdminApiCall,
    changePasswordAdminApiCall,
    logout: logoutAdminApiCall,
    getAdminAccountApiCall,
    refreshAdminTokenApiCall,
    listAdminsApiCall,
    createAdminApiCall,
    getAdminByIdApiCall,
    updateAdminApiCall,
    suspendAdminApiCall,
    toggleAdminStatusApiCall,
    getAdminActivityLogsApiCall,
    getActivityLogsApiCall,
    getActivitySummaryApiCall,
    getRecentActivityApiCall,
    getDashboardOverviewApiCall,
    getUsersListApiCall,
    getUserByIdApiCall,
    updateUserApiCall,
    toggleUserStatusApiCall,
    clearErrors,
    showSnackbar,
    loadANewPage,

    getRoomsListApiCall,
    getRoomByIdApiCall,
    updateRoomApiCall,
    toggleRoomStatusApiCall,

    getActiveSessionsApiCall,
    getSessionStatisticsApiCall,
    terminateSessionApiCall,
    cleanupExpiredSessionsApiCall,
    getPaymentsListApiCall,
    getPaymentStatisticsApiCall,
    getPaymentByIdApiCall,
    updatePaymentStatusApiCall,
    exportPaymentsApiCall,
    getUserAnalyticsApiCall,
    getRoomAnalyticsApiCall,
    getPaymentAnalyticsApiCall,
    getDashboardAnalyticsApiCall,
    getSystemHealthMetricsApiCall,
    getLoginActivityAnalyticsApiCall,

    // Platform Economy API calls
    getAllEconomySettingsApiCall,
    getEconomyStatisticsApiCall,
    getCoinEconomyApiCall,
    updateCoinEconomyApiCall,
    getRoomLimitsApiCall,
    updateRoomLimitsApiCall,
    getXPRewardsApiCall,
    updateXPRewardsApiCall,
    getGeneralSettingsApiCall,
    updateGeneralSettingsApiCall,
    getFeeLimitsApiCall,
    updateFeeLimitsApiCall,
    updateBulkSettingsApiCall,
    createNewSettingApiCall,
    getSettingByKeyApiCall,
    updateSettingByKeyApiCall,
    deleteSettingByKeyApiCall,

    // Coin Packages
    getAllCoinPackagesApiCall,
    createCoinPackageApiCall,
    updateCoinPackageApiCall,
    deleteCoinPackageApiCall,

    // Subscription Plans
    getAllSubscriptionPlansApiCall,
    createSubscriptionPlanApiCall,
    updateSubscriptionPlanApiCall,
    deleteSubscriptionPlanApiCall,

    // System Config
    getSystemConfigApiCall,
    updateSystemConfigApiCall,

    // Ad Management API calls
    getAdminAdsApiCall,
    getAdAnalyticsApiCall,
    createAdApiCall,
    getAdByIdApiCall,
    updateAdApiCall,
    deleteAdApiCall,
    activateAdApiCall,
    pauseAdApiCall,
    checkAdImpressionGoalApiCall,

    // Notification API calls
    getNotificationsApiCall,
    getNotificationByIdApiCall,
    createNotificationApiCall,
    updateNotificationApiCall,
    deleteNotificationApiCall,
  };
}

export default useAdminContext;
